import React from "react";
import TextField from "@mui/material/TextField";
import Autocomplete from '@mui/material/Autocomplete';
import LocationOnIcon from "@mui/icons-material/LocationOn";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import makeStyles from '@mui/styles/makeStyles';
import Paper from "@mui/material/Paper";
import parse from "autosuggest-highlight/parse";
import throttle from "lodash/throttle";

function loadScript(src, position, id) {
	if (!position) {
		return;
	}

	const script = document.createElement("script");
	script.setAttribute("async", "");
	script.setAttribute("id", id);
	script.src = src;
	position.appendChild(script);
}

const autocompleteService = {current: null};

const useStyles = makeStyles((theme) => ({
	root: {
		"&&": {
			display: "block",
			width: "100%",
			minWidth: 0,
		},
		"&& .MuiFormControl-root, && .MuiTextField-root, && .MuiAutocomplete-inputRoot": {
			width: "100%",
			minWidth: 0,
		},
	},
	icon: {
		color: theme.palette.text.secondary,
		marginRight: theme.spacing(2)
	},
	paper: {
		background: "var(--atl-surface-solid) !important",
		backgroundColor: "var(--atl-surface-solid) !important",
		backgroundImage: "none !important",
		color: "var(--atl-text) !important",
		border: "1px solid var(--atl-border)",
		borderRadius: "14px !important",
		boxShadow: "var(--atl-shadow) !important",
		backdropFilter: "none !important",
		WebkitBackdropFilter: "none !important",
		marginTop: 4,
		overflowY: "auto",
		scrollbarWidth: "thin",
	},
}));

const GoogleMaps = ({value, setValue, inputValue, setInputValue, label, disabled = false, size = "medium"}) => {
	const classes = useStyles();
	const [options, setOptions] = React.useState([]);
	const loaded = React.useRef(false);

	let KEY = "AIzaSyB-vsXmMIvCeg3kQs7OL0curZZtKQABZ80";

	if (typeof window !== "undefined" && !loaded.current) {
		if (!document.querySelector("#google-maps")) {
			loadScript(
				"https://maps.googleapis.com/maps/api/js?key=" + KEY + "&libraries=places",
				document.querySelector("head"),
				"google-maps"
			);
		}

		loaded.current = true;
	}

	const fetch = React.useMemo(
		() =>
			throttle((request, callback) => {
				autocompleteService.current.getPlacePredictions(request, callback);
			}, 200),
		[]
	);

	React.useEffect(() => {
		let active = true;

		if (!autocompleteService.current && window.google) {
			autocompleteService.current = new window.google.maps.places.AutocompleteService();
		}
		if (!autocompleteService.current) {
			return undefined;
		}

		if (inputValue === "") {
			setOptions(value ? [value] : []);
			return undefined;
		}

		fetch({input: inputValue}, (results) => {
			if (active) {
				let newOptions = [];

				if (value) {
					newOptions = [value];
				}

				if (results) {
					newOptions = [...newOptions, ...results];
				}

				setOptions(newOptions);
			}
		});

		return () => {
			active = false;
		};
	}, [value, inputValue, fetch]);

	return (
		<Autocomplete
			className={classes.root}
			disabled={disabled}
			fullWidth
			id="google-map-demo"
			getOptionLabel={(option) =>
				typeof option === "string" ? option : option.description
			}
			filterOptions={(x) => x}
			options={options}
			autoComplete
			includeInputInList
			filterSelectedOptions
			value={value}
			slots={{paper: Paper}}
			slotProps={{
				paper: {
					className: classes.paper,
				},
			}}
			size={size}
			onChange={(event, newValue) => {
				setOptions(newValue ? [newValue, ...options] : options);
				setValue(newValue);
			}}
			onInputChange={(event, newInputValue) => {
				setInputValue(newInputValue);
			}}
			renderInput={(params) => (
				<TextField
					{...params}
					label={label}
					size={size}
					variant="outlined"
					fullWidth
				/>
			)}
			renderOption={(props, option) => {
				const matches = (option && option.structured_formatting) ? option.structured_formatting.main_text_matched_substrings : [];
				const parts = parse(
					option?.structured_formatting?.main_text,
					matches?.map((match) => [match.offset, match.offset + match.length])
				);

				return (
					<li {...props}>
						<Grid container alignItems="center">
						<Grid item>
							<LocationOnIcon className={classes.icon}/>
						</Grid>
						<Grid item xs>
							{parts.map((part, index) => (
								<span
									key={index}
									style={{fontWeight: part.highlight ? 700 : 400}}
								>
                  {part.text}
                </span>
							))}

							<Typography variant="body2" color="textSecondary">
								{option.structured_formatting.secondary_text}
							</Typography>
						</Grid>
						</Grid>
					</li>
				);
			}}
		/>
	);
}

export default React.memo(GoogleMaps);
